import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import ProgressBar from '../../../../Shared/ProgressBar';
import { ReactComponent as AssignUser } from '../../../../../images/icons/assign_user.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import { getCustomerList } from '../../../../../store/customers/actionCreators';
import { Owner } from '../../../../../store/customers/customerTypes';
import { Device } from '../../../../../store/devices/deviceTypes';
import { borderGrey, lightBlack, mediumGrey } from '../../../../../mainTheme';

export const ExistingPatientAssignment = ({
  onAdd,
  onCancel,
  selectedDeviceDetails,
}: {
  onAdd: (customer_id: number) => void;
  onCancel: () => void;
  selectedDeviceDetails: Device | null;
}) => {
  const dispatch = useDispatch();
  const { owners, isLoading } = useSelector(
    (state: RootState) => state.customers,
  );
  const { isLoading: deviceLoading } = useSelector(
    (state: RootState) => state.devices,
  );

  useEffect(() => {
    if (!owners) {
      dispatch(getCustomerList());
    }
  }, [dispatch, owners]);

  const availablePatients = React.useMemo(() => {
    if (!owners || !selectedDeviceDetails) return [];

    return owners.filter((owner) => {
      // Filter out patients who already have devices assigned
      const noDeviceAssigned = !owner.device_serial_number && !owner.device_id;

      // Facility matching logic
      const facilityMatch = selectedDeviceDetails.facility_id
        ? owner.facility_id === selectedDeviceDetails.facility_id // Show only patients from same facility
        : true; // If device has no facility, show all unassigned patients

      return noDeviceAssigned && facilityMatch;
    });
  }, [owners, selectedDeviceDetails]);

  const [selectedPatient, setSelectedPatient] = useState<Owner | null>(null);
  const [open, setOpen] = useState(false);

  const handleAssign = () => {
    onAdd(selectedPatient?.customer_id || 0);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <ProgressBar currentStep={2} totalSteps={2} />
      <AssignUser />
      <Typography variant="subtitle1">Assign to existing patient</Typography>

      <Autocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={availablePatients}
        getOptionLabel={(option) =>
          `${option.name}, ${
            option.date_of_birth ? 'DOB: ' + option.date_of_birth : ''
          }`
        }
        onChange={(_event, newValue) => {
          setSelectedPatient(newValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Patient name and DOB"
            fullWidth
            margin="normal"
            variant="outlined"
            sx={{
              '& .MuiInputBase-input': {
                color: lightBlack,
                fontSize: '16px',
              },
              '& .MuiInputLabel-root': {
                color: lightBlack,
                fontSize: '16px',
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: borderGrey,
                  borderRadius: '4px',
                },
                '&:hover fieldset': {
                  borderColor: borderGrey,
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#7C3AED',
                },
              },
            }}
          />
        )}
        blurOnSelect
        popupIcon={
          <FontAwesomeIcon
            icon={faAngleDown}
            style={{ color: mediumGrey, fontSize: '24px' }}
          />
        }
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.customer_id}
            style={{
              color: lightBlack,
              padding: '8px 16px',
              fontSize: '16px',
            }}
          >
            <span style={{ fontWeight: 500 }}>{option.name}</span>
            {option.date_of_birth && (
              <span style={{ color: mediumGrey }}>
                , DOB: {option.date_of_birth}
              </span>
            )}
          </li>
        )}
        ListboxProps={{
          style: {
            maxHeight: '280px',
            border: `1px solid ${borderGrey}`,
            borderRadius: '4px',
            marginTop: '4px',
          },
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button variant="text" color="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleAssign}
          disabled={!selectedPatient || isLoading || deviceLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Add'}
        </Button>
      </Box>
    </Box>
  );
};
